import './polyfills/process-env'
import './styles/index.css'

import '@livekit/components-styles'
import { StyledEngineProvider } from '@mui/joy/styles'
import * as ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { AuthInitializer } from './components/AuthInitializer'
import setupAxios from './setup/SetupAxios'
import store from './store'
import { initAmplitude, initGA } from 'services/analytics'
import { initErrorTracking } from './services/errorTracking'
import { isSecretLinkPage } from './utils/secretLinkPage'

const amplitudeApiKey = process.env.REACT_APP_AMPLITUDE_API_KEY
const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID
const gaApiSecret = process.env.REACT_APP_GA_API_SECRET

// Not on a page whose address is a credential (a signing link): those must never reach a third party, whatever an
// SDK's defaults become. GTM and the Meta Pixel are kept off those pages the same way.
const analyticsAllowed = process.env.NODE_ENV !== 'test' && !isSecretLinkPage()

// Initialize on module load (except in tests)
if (analyticsAllowed && amplitudeApiKey) {
    initAmplitude(amplitudeApiKey, { setAsGlobal: true })
}

// Initialize GA4 (mirrors Amplitude events). No-ops without both measurement id and api secret.
if (analyticsAllowed && gaMeasurementId && gaApiSecret) {
    initGA(gaMeasurementId, gaApiSecret)
}

// Only import telemetry setup when not in test environment
// eslint-disable-next-line @typescript-eslint/no-require-imports
const telemetrySetup = process.env.NODE_ENV !== 'test' ? require('./telemetry/setup') : null

// Reset extension flags on page reload — extension content script will re-set to true if installed
// Skip on onboarding pages to preserve extension state during onboarding flow
if (!window.location.pathname.startsWith('/onboarding')) {
    localStorage.setItem('hasExtension', 'false')
    localStorage.removeItem('numeo-extension-connected')
}

setupAxios(() => {
    console.error('User is not authorized')
})

if (process.env.NODE_ENV !== 'test') {
    initErrorTracking()
}

const initializeApp = async () => {
    try {
        if (process.env.REACT_APP_ENABLE_TELEMETRY === 'true' && process.env.REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT && telemetrySetup) {
            console.log('Initializing OpenTelemetry...')
            await telemetrySetup.ensureTelemetryInitialized()
            console.log('OpenTelemetry initialization completed')
        } else {
            console.log('OpenTelemetry disabled or not configured')
        }
    } catch (error) {
        console.error('Failed to initialize OpenTelemetry:', error)
    }

    const root = ReactDOM.createRoot(document.querySelector('#root')!)
    root.render(
        <StyledEngineProvider injectFirst>
            <Provider store={store}>
                <BrowserRouter>
                    <AuthInitializer>
                        <App />
                    </AuthInitializer>
                </BrowserRouter>
            </Provider>
        </StyledEngineProvider>
    )
}

initializeApp().catch((error) => {
    console.error('Failed to initialize application:', error)
})
